import { useRef, useCallback } from 'react';
import { TextField, Box, IconButton, Tooltip, Divider } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  Title,
  Code,
  FormatQuote,
  FormatListBulleted,
  FormatListNumbered,
  InsertLink,
  HorizontalRule,
  DataObject,
  TableChart,
  CheckBox,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const EditorWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc',
}));

const ToolbarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '1px',
  padding: '7px 10px',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
  backgroundColor: theme.palette.mode === 'dark' ? '#0c1421' : '#f1f5f9',
  minHeight: '44px',
}));

const ToolbarButton = styled(IconButton)(({ theme }) => ({
  padding: '5px',
  borderRadius: '7px',
  color: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(14, 165, 233, 0.12)' : 'rgba(14, 165, 233, 0.08)',
    color: theme.palette.mode === 'dark' ? '#38bdf8' : '#0284c7',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '17px',
  },
}));

const ToolbarDivider = styled(Divider)(({ theme }) => ({
  margin: '0 5px',
  height: '18px',
  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.09)',
}));

const StyledEditor = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiInputBase-root': {
    height: '100%',
    alignItems: 'flex-start',
    padding: '0 !important',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    backgroundColor: theme.palette.mode === 'dark' ? '#0f172a' : '#ffffff',
    fontFamily: '"SF Mono", "Fira Code", "Fira Mono", Menlo, Consolas, monospace',
    fontSize: '13.5px',
    lineHeight: 1.65,
    color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#1e293b',

    '& textarea': {
      padding: '18px 20px',
      height: 'calc(100% - 36px) !important',
      overflowY: 'auto !important',
    },

    '& fieldset': {
      border: 'none',
    },
  },
  backgroundColor: theme.palette.mode === 'dark' ? '#0f172a' : '#ffffff',
}));

export default function Editor({ value, onChange }) {
  const inputRef = useRef(null);

  const getTextarea = useCallback(() => {
    if (!inputRef.current) return null;
    return inputRef.current.querySelector('textarea');
  }, []);

  const insertFormatting = useCallback((before, after = '', placeholder = '') => {
    const textarea = getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const textToInsert = selectedText || placeholder;
    const newValue =
      value.substring(0, start) + before + textToInsert + after + value.substring(end);

    onChange(newValue);

    requestAnimationFrame(() => {
      textarea.focus();
      if (selectedText) {
        textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
      } else {
        textarea.setSelectionRange(start + before.length, start + before.length + placeholder.length);
      }
    });
  }, [value, onChange, getTextarea]);

  const insertAtLineStart = useCallback((prefix) => {
    const textarea = getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const newValue = value.substring(0, lineStart) + prefix + value.substring(lineStart);

    onChange(newValue);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, start + prefix.length);
    });
  }, [value, onChange, getTextarea]);

  const toolbarActions = [
    { icon: <Title />, tooltip: 'Heading (H2)', action: () => insertAtLineStart('## ') },
    { divider: true },
    { icon: <FormatBold />, tooltip: 'Bold', action: () => insertFormatting('**', '**', 'bold text') },
    { icon: <FormatItalic />, tooltip: 'Italic', action: () => insertFormatting('*', '*', 'italic text') },
    { icon: <FormatStrikethrough />, tooltip: 'Strikethrough', action: () => insertFormatting('~~', '~~', 'text') },
    { divider: true },
    { icon: <Code />, tooltip: 'Inline Code', action: () => insertFormatting('`', '`', 'code') },
    { icon: <DataObject />, tooltip: 'Code Block', action: () => insertFormatting('\n```\n', '\n```\n', 'code here') },
    { divider: true },
    { icon: <FormatListBulleted />, tooltip: 'Bullet List', action: () => insertAtLineStart('- ') },
    { icon: <FormatListNumbered />, tooltip: 'Numbered List', action: () => insertAtLineStart('1. ') },
    { icon: <CheckBox />, tooltip: 'Task List', action: () => insertAtLineStart('- [ ] ') },
    { divider: true },
    { icon: <InsertLink />, tooltip: 'Link', action: () => insertFormatting('[', '](url)', 'link text') },
    { icon: <FormatQuote />, tooltip: 'Blockquote', action: () => insertAtLineStart('> ') },
    { icon: <HorizontalRule />, tooltip: 'Horizontal Rule', action: () => insertFormatting('\n---\n', '') },
    { icon: <TableChart />, tooltip: 'Table', action: () => insertFormatting('\n| Header | Header |\n| ------ | ------ |\n| Cell   | Cell   |\n', '') },
  ];

  return (
    <EditorWrapper>
      <ToolbarContainer>
        {toolbarActions.map((item, idx) =>
          item.divider ? (
            <ToolbarDivider key={idx} orientation="vertical" flexItem />
          ) : (
            <Tooltip key={idx} title={item.tooltip} arrow placement="top">
              <ToolbarButton size="small" onClick={item.action}>
                {item.icon}
              </ToolbarButton>
            </Tooltip>
          )
        )}
      </ToolbarContainer>
      <StyledEditor
        ref={inputRef}
        multiline
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        placeholder="Start typing Markdown here..."
      />
    </EditorWrapper>
  );
}
